import { Type, Hash, List, Table, FileSignature, CheckSquare, User, Mail, Phone, Calendar as CalendarIcon, ToggleLeft, FileText, Database, LayoutTemplate } from 'lucide-react';
// Helper function to create block commands
const createBlockCommand = (id, label, description, icon, blockType) => ({
    id,
    path: [id],
    label,
    description,
    icon,
    category: 'blocks',
    action: {
        type: 'insert-block',
        blockType
    }
});
export const COMMAND_REGISTRY = [
    // Basic block commands (existing)
    createBlockCommand('paragraph', 'Text', 'Plain text paragraph', Type, 'paragraph'),
    createBlockCommand('heading1', 'Heading 1', 'Large section heading', Hash, 'heading1'),
    createBlockCommand('heading2', 'Heading 2', 'Medium section heading', Hash, 'heading2'),
    createBlockCommand('heading3', 'Heading 3', 'Small section heading', Hash, 'heading3'),
    createBlockCommand('list', 'Bulleted List', 'Create a simple list', List, 'list'),
    createBlockCommand('table', 'Table', 'Add a table with data', Table, 'table'),
    // /sign commands
    {
        id: 'sign',
        path: ['sign'],
        label: 'Sign',
        description: 'Signature and signing workflows',
        icon: FileSignature,
        category: 'actions',
        children: [
            {
                id: 'sign-assign',
                path: ['sign', 'assign'],
                label: 'Assign Signature',
                description: 'Assign a signature block to someone',
                icon: User,
                category: 'actions',
                parameters: [
                    {
                        name: 'email',
                        type: 'email',
                        placeholder: 'Enter email address',
                        required: true
                    }
                ],
                action: {
                    type: 'insert-signature',
                    data: { assignSignature: true }
                }
            }
        ]
    },
    // /form commands - quick form field insertion
    {
        id: 'form',
        path: ['form'],
        label: 'Form',
        description: 'Quick form field insertion',
        icon: CheckSquare,
        category: 'blocks',
        children: [
            {
                id: 'form-text',
                path: ['form', 'text'],
                label: 'Text Field',
                description: 'Insert a text input field',
                icon: Type,
                category: 'blocks',
                parameters: [
                    {
                        name: 'label',
                        type: 'text',
                        placeholder: 'Field label (e.g., "Full Name")',
                        required: true
                    }
                ],
                action: {
                    type: 'insert-form',
                    data: { fieldType: 'text' }
                }
            },
            {
                id: 'form-email',
                path: ['form', 'email'],
                label: 'Email Field',
                description: 'Insert an email input field',
                icon: Mail,
                category: 'blocks',
                parameters: [
                    {
                        name: 'label',
                        type: 'text',
                        placeholder: 'Field label (e.g., "Email Address")',
                        required: true
                    }
                ],
                action: {
                    type: 'insert-form',
                    data: { fieldType: 'email' }
                }
            },
            {
                id: 'form-phone',
                path: ['form', 'phone'],
                label: 'Phone Field',
                description: 'Insert a phone input field',
                icon: Phone,
                category: 'blocks',
                parameters: [
                    {
                        name: 'label',
                        type: 'text',
                        placeholder: 'Field label (e.g., "Phone Number")',
                        required: true
                    }
                ],
                action: {
                    type: 'insert-form',
                    data: { fieldType: 'phone' }
                }
            },
            {
                id: 'form-number',
                path: ['form', 'number'],
                label: 'Number Field',
                description: 'Insert a number input field',
                icon: Hash,
                category: 'blocks',
                parameters: [
                    {
                        name: 'label',
                        type: 'text',
                        placeholder: 'Field label (e.g., "Amount")',
                        required: true
                    }
                ],
                action: {
                    type: 'insert-form',
                    data: { fieldType: 'number' }
                }
            },
            {
                id: 'form-date',
                path: ['form', 'date'],
                label: 'Date Field',
                description: 'Insert a date picker field',
                icon: CalendarIcon,
                category: 'blocks',
                parameters: [
                    {
                        name: 'label',
                        type: 'text',
                        placeholder: 'Field label (e.g., "Birth Date")',
                        required: true
                    }
                ],
                action: {
                    type: 'insert-form',
                    data: { fieldType: 'date' }
                }
            },
            {
                id: 'form-checkbox',
                path: ['form', 'checkbox'],
                label: 'Checkbox',
                description: 'Insert a checkbox field',
                icon: ToggleLeft,
                category: 'blocks',
                parameters: [
                    {
                        name: 'label',
                        type: 'text',
                        placeholder: 'Field label (e.g., "I agree to terms")',
                        required: true
                    }
                ],
                action: {
                    type: 'insert-form',
                    data: { fieldType: 'checkbox' }
                }
            }
        ]
    },
    // /data commands - placeholders for future
    {
        id: 'data',
        path: ['data'],
        label: 'Data',
        description: 'Fetch and display external data',
        icon: Database,
        category: 'data',
        children: [
            {
                id: 'data-fetch',
                path: ['data', 'fetch'],
                label: 'Fetch from URL',
                description: 'Fetch data from an API endpoint',
                icon: Database,
                category: 'data',
                parameters: [
                    {
                        name: 'url',
                        type: 'url',
                        placeholder: 'API endpoint URL',
                        required: true
                    }
                ],
                action: {
                    type: 'fetch-data'
                }
            }
        ]
    },
    // /template commands - placeholders for future
    {
        id: 'template',
        path: ['template'],
        label: 'Template',
        description: 'Insert pre-made templates',
        icon: LayoutTemplate,
        category: 'templates',
        children: [
            {
                id: 'template-load',
                path: ['template', 'load'],
                label: 'Load Template',
                description: 'Load a pre-made document template',
                icon: FileText,
                category: 'templates',
                parameters: [
                    {
                        name: 'templateId',
                        type: 'text',
                        placeholder: 'Template ID',
                        required: true
                    }
                ],
                action: {
                    type: 'load-template'
                }
            }
        ]
    }
];
// Utility functions
export function findCommandByPath(path) {
    const searchInCommands = (commands, remainingPath) => {
        if (remainingPath.length === 0)
            return undefined;
        const current = remainingPath[0];
        const command = commands.find(cmd => cmd.path[cmd.path.length - 1] === current);
        if (!command)
            return undefined;
        if (remainingPath.length === 1)
            return command;
        if (!command.children)
            return undefined;
        return searchInCommands(command.children, remainingPath.slice(1));
    };
    return searchInCommands(COMMAND_REGISTRY, path);
}
export function parseCommandInput(input) {
    // Remove leading slash
    const cleaned = input.startsWith('/') ? input.slice(1) : input;
    // Split by /
    const parts = cleaned.split('/').filter(p => p.trim().length > 0);
    if (parts.length === 0) {
        return { path: [], parameters: [], isComplete: false };
    }
    // Try to find matching command
    const command = findCommandByPath(parts);
    if (!command) {
        // Might be partial path
        return { path: parts, parameters: [], isComplete: false };
    }
    // Check if we have enough parts for parameters
    const pathLength = command.path.length;
    const parameters = parts.slice(pathLength);
    const requiredParams = command.parameters?.filter(p => p.required).length || 0;
    return {
        path: parts.slice(0, pathLength),
        parameters,
        isComplete: parameters.length >= requiredParams
    };
}
export function getAvailableCommands(currentPath) {
    if (currentPath.length === 0) {
        return COMMAND_REGISTRY;
    }
    // Find parent command
    if (currentPath.length === 1) {
        const parent = COMMAND_REGISTRY.find(cmd => cmd.id === currentPath[0]);
        return parent?.children || [];
    }
    const parent = findCommandByPath(currentPath.slice(0, -1));
    return parent?.children || [];
}
